import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface ShearParams {
  points: Array<{ x: number; y: number }>;
  undefinedAreas: "wrap" | "repeat";
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Shear: Filter<ShearParams> = {
  id: "shear",
  displayName: "Shear",
  defaultParams: { points: [{ x: 0, y: 0 }, { x: 64, y: 0 }, { x: 128, y: 0 }, { x: 255, y: 0 }], undefinedAreas: "wrap" },

  apply(input: PixelBuffer, params: ShearParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const pts = params.points.length >= 2 ? params.points : [{ x: 0, y: 0 }, { x: 255, y: 0 }];

    function getShearX(normalizedY: number): number {
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i]!, p1 = pts[i + 1]!;
        const y0 = p0.x / 255, y1 = p1.x / 255;
        if (normalizedY >= y0 && normalizedY <= y1) {
          const t = y1 === y0 ? 0 : (normalizedY - y0) / (y1 - y0);
          return p0.y + (p1.y - p0.y) * t;
        }
      }
      return pts[pts.length - 1]!.y;
    }

    for (let y = 0; y < h; y++) {
      const ny = y / h;
      const shift = getShearX(ny) * w / 255;

      for (let x = 0; x < w; x++) {
        let sx = x - shift;
        if (params.undefinedAreas === "wrap") {
          sx = ((sx % w) + w) % w;
        } else {
          sx = Math.max(0, Math.min(w - 1, sx));
        }

        const ix = Math.floor(sx);
        const fx = sx - ix;
        const x0 = Math.max(0, Math.min(w - 1, ix));
        const x1 = Math.max(0, Math.min(w - 1, ix + 1));

        const o = (y * w + x) * 4;
        for (let c = 0; c < 4; c++) {
          dst[o + c] = Math.round(src[(y * w + x0) * 4 + c]! * (1 - fx) + src[(y * w + x1) * 4 + c]! * fx);
        }
      }
    }
    return out;
  },
};
